import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { AlphaBadge } from '../components/AlphaNotice.js';
import { Link, useSearchParams } from 'react-router-dom';
import { MAX_NAME_LENGTH, type ProfileColorId } from '@tv-casino/shared';
import { getSession, signOut, updateProfile, type SessionUser } from '../lib/session.js';
import { InviteCard } from '../components/InviteCard.js';
import {
  fetchMemberView,
  fetchMyPlay,
  fetchMyReferrals,
  lookupCasino,
  updateMyMembership,
  type CasinoLookup,
} from '../lib/casinoApi.js';
import { memberIdentity } from '../lib/storage.js';
import { useTvConfig } from '../lib/useTvConfig.js';
import { statLinesOf } from '@tv-casino/viewmodels/components/memberTableModel.js';
import { profileModel } from '@tv-casino/viewmodels/routes/profileModel.js';
import type { CasinoMemberView, ProfilePlayResponse, ReferralSummary } from '@tv-casino/shared';
import styles from './Profile.module.css';

/**
 * One page for "who am I here": the account when someone is signed in, the
 * membership this phone holds at a casino, or both.
 *
 * Which casino is in the URL (`/profile?casino=VELV`); with none, it falls to
 * the last venue this phone sat down in, which is the one a player means when
 * they open their profile mid-night.
 *
 * The name and the colour are written to **both** halves at once. That is the
 * model the rest of the app already states — one account, one seat name — and
 * it means a player never has to wonder which of two profiles they just
 * edited. At the table the colour becomes the seat's when nobody sat down in
 * it first — two players are free to wear the same colour here, and the felt
 * still has to tell their seats apart.
 */
export function Profile() {
  const [params] = useSearchParams();
  const [session, setSession] = useState<SessionUser | null>(null);
  const [play, setPlay] = useState<ProfilePlayResponse | null>(null);
  const [referrals, setReferrals] = useState<ReferralSummary | null>(null);
  const [member, setMember] = useState<CasinoMemberView | null>(null);
  const [casino, setCasino] = useState<CasinoLookup | null>(null);
  const [loading, setLoading] = useState(true);
  const [nameField, setNameField] = useState('');
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const serverConfig = useTvConfig(window.location.origin);

  /** The venue in the URL, else the last one this phone sat down in. */
  const code = params.get('casino') ?? memberIdentity.lastVenue();

  useEffect(() => {
    let live = true;
    const load = async () => {
      const user = await getSession().catch(() => null);
      if (!live) return;
      setSession(user);

      // Signed out there is no play history to ask for, and asking would
      // answer 401 — which is not an error worth showing anybody.
      const [history, invited] = user
        ? await Promise.all([
            fetchMyPlay().catch(() => null),
            fetchMyReferrals().catch(() => null),
          ])
        : [null, null];
      if (!live) return;
      setPlay(history);
      setReferrals(invited);

      if (code) {
        const stored = memberIdentity.get(code);
        const [venue, card] = await Promise.all([
          lookupCasino(code).catch(() => null),
          stored ? fetchMemberView(code, stored.id, stored.token).catch(() => null) : null,
        ]);
        if (!live) return;
        setCasino(venue);
        setMember(card);
      }
      if (live) setLoading(false);
    };
    void load();
    return () => {
      live = false;
    };
  }, [code]);

  const memberLines = useMemo(
    () => (member ? statLinesOf({ ...member, casinoId: member.casinoId, memberId: member.memberId }) : []),
    [member],
  );

  const vm = useMemo(
    () =>
      profileModel({
        session,
        play,
        referrals,
        origin: window.location.origin,
        publicUrl: serverConfig?.publicUrl,
        member,
        casino,
        casinoCode: code,
        memberLines,
      }),
    [session, play, referrals, serverConfig, member, casino, code, memberLines],
  );

  // The field follows the model until the user types into it; after that it is
  // theirs, and a background refresh must not yank it back.
  useEffect(() => {
    if (!dirty) setNameField(vm.editableName);
  }, [vm.editableName, dirty]);

  /**
   * One writer for both halves. Either may be absent, and a half that is
   * absent is simply not written — never invented.
   */
  const save = useCallback(
    async (patch: { name?: string; color?: ProfileColorId | null }) => {
      setBusy(true);
      setError(null);
      setSaved(false);
      try {
        if (session) {
          const next = await updateProfile({
            ...(patch.name !== undefined ? { playerName: patch.name } : {}),
            ...(patch.color !== undefined ? { profileColor: patch.color } : {}),
          });
          setSession(next);
        }
        if (member && code) {
          const stored = memberIdentity.get(code);
          const next = await updateMyMembership(code, member.memberId, stored?.token ?? null, patch);
          setMember(next);
        }
        setDirty(false);
        setSaved(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not save your profile.');
      } finally {
        setBusy(false);
      }
    },
    [session, member, code],
  );

  const onSubmitName = (event: FormEvent) => {
    event.preventDefault();
    void save({ name: nameField.trim() });
  };

  const onSignOut = async () => {
    await signOut().catch(() => undefined);
    setSession(null);
    setPlay(null);
    setReferrals(null);
  };

  const accent = { '--accent': vm.accentHex } as React.CSSProperties;

  if (loading) {
    return (
      <main className={styles.wrap}>
        <p className={styles.loading}>Reading your profile…</p>
      </main>
    );
  }

  return (
    <main className={styles.wrap} style={accent}>
      <header className={styles.head}>
        <div className={styles.avatar} aria-hidden="true">
          {vm.initials}
        </div>
        <div className={styles.headText}>
          <h1 className={styles.title}>
            {vm.headline}
            <AlphaBadge />
          </h1>
          <p className={styles.subhead}>{vm.subhead}</p>
        </div>
      </header>

      {vm.emptyLine ? (
        <section className={styles.card}>
          <p className={styles.empty}>{vm.emptyLine}</p>
          <div className={styles.actions}>
            <Link className={styles.link} to="/casino">
              Sign in
            </Link>
            <Link className={styles.link} to="/">
              Join a casino
            </Link>
          </div>
        </section>
      ) : null}

      {vm.editable ? (
        <section className={styles.card} aria-label="Profile">
          <form className={styles.nameForm} onSubmit={onSubmitName}>
            <label className={styles.label} htmlFor="profile-name">
              Display name
            </label>
            <div className={styles.nameRow}>
              <input
                id="profile-name"
                className={styles.input}
                value={nameField}
                maxLength={MAX_NAME_LENGTH}
                placeholder="Pick a name"
                onChange={(e) => {
                  setNameField(e.target.value);
                  setDirty(true);
                  setSaved(false);
                }}
              />
              <button className={styles.save} type="submit" disabled={busy || !nameField.trim()}>
                Save
              </button>
            </div>
            <p className={styles.hint}>
              Used everywhere you play — the felt, the leaderboard and the TV.
            </p>
          </form>

          <fieldset className={styles.colors}>
            <legend className={styles.label}>Your colour</legend>
            <div className={styles.swatches}>
              {vm.colors.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={c.active ? `${styles.swatch} ${styles.swatchOn}` : styles.swatch}
                  style={{ '--swatch': c.hex } as React.CSSProperties}
                  aria-pressed={c.active}
                  aria-label={c.label}
                  title={c.label}
                  disabled={busy}
                  // Tapping the hue already worn clears it, so "no pick" stays
                  // reachable without a separate button for it.
                  onClick={() => void save({ color: c.active ? null : c.id })}
                />
              ))}
            </div>
            <p className={styles.hint}>
              Worn on your seat and chips at the table, and beside your name on the floor,
              the chip desk and the TV board — unless someone at the table picked it first.
            </p>
          </fieldset>

          {error ? (
            <p className={styles.error} role="alert">
              {error}
            </p>
          ) : null}
          {saved && !error ? <p className={styles.saved}>Saved.</p> : null}
        </section>
      ) : null}

      {vm.membership ? (
        <section className={styles.card} aria-label="Membership">
          <div className={styles.cardHead}>
            <h2 className={styles.cardTitle}>{vm.membership.venueName}</h2>
            {vm.membership.joinCode ? (
              <Link className={styles.link} to={`/play?casino=${vm.membership.joinCode}`}>
                Back to the floor
              </Link>
            ) : null}
          </div>
          <div className={styles.rail}>
            <div className={styles.bigStat}>
              <span className={styles.bigValue}>{vm.membership.chips}</span>
              <span className={styles.bigLabel}>chips</span>
            </div>
            <div className={styles.bigStat}>
              <span
                className={vm.membership.down ? `${styles.bigValue} ${styles.down}` : `${styles.bigValue} ${styles.up}`}
              >
                {vm.membership.swing}
              </span>
              <span className={styles.bigLabel}>on {vm.membership.buyIn} staked</span>
            </div>
          </div>
          {vm.membership.emptyLine ? (
            <p className={styles.empty}>{vm.membership.emptyLine}</p>
          ) : (
            <dl className={styles.stats}>
              {vm.membership.lines.map((line) => (
                <div className={styles.stat} key={line.label}>
                  <dt className={styles.statLabel}>{line.label}</dt>
                  <dd className={styles.statValue}>{line.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </section>
      ) : null}

      {vm.invite ? <InviteCard className={styles.card} invite={vm.invite} /> : null}

      {vm.account ? (
        <section className={styles.card} aria-label="Account">
          <div className={styles.cardHead}>
            <h2 className={styles.cardTitle}>Account</h2>
            <button className={styles.link} type="button" onClick={() => void onSignOut()}>
              Sign out
            </button>
          </div>
          <p className={styles.accountLine}>{vm.account.displayName}</p>
          <p className={styles.accountSub}>{vm.account.email}</p>
          <p className={styles.accountSub}>{vm.account.playerNameLine}</p>

          <h3 className={styles.sectionTitle}>Venues played</h3>
          {vm.account.emptyVenuesLine ? (
            <p className={styles.empty}>{vm.account.emptyVenuesLine}</p>
          ) : (
            <ul className={styles.venues}>
              {vm.account.venues.map((v) => (
                <li className={styles.venue} key={v.casinoId}>
                  <Link className={styles.venueName} to={v.href}>
                    {v.name}
                    {v.owned ? <span className={styles.owned}>yours</span> : null}
                  </Link>
                  <span className={styles.venueMeta}>
                    {v.chips} chips · {v.rounds}
                  </span>
                  <span className={v.down ? `${styles.venueNet} ${styles.down}` : `${styles.venueNet} ${styles.up}`}>
                    {v.net}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {vm.account.lifetime.length > 0 ? (
            <>
              <h3 className={styles.sectionTitle}>Lifetime</h3>
              <dl className={styles.stats}>
                {vm.account.lifetime.map((line) => (
                  <div className={styles.stat} key={line.label}>
                    <dt className={styles.statLabel}>{line.label}</dt>
                    <dd className={styles.statValue}>{line.value}</dd>
                  </div>
                ))}
              </dl>
            </>
          ) : null}
        </section>
      ) : null}
    </main>
  );
}
